import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, ArrowLeft, ArrowRight, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useAuth } from '../../contexts/AuthContext';
import { UserService } from '../../api/services/UserService';
import { OnboardingLayout } from './OnboardingLayout';
import { BrandStep } from './steps/BrandStep';
import { ProfileStep } from './steps/ProfileStep';
import { HomeAddressStep } from './steps/HomeAddressStep';
import { BaseLocationStep } from './steps/BaseLocationStep';
import { RadiusStep } from './steps/RadiusStep';
import { HoursStep } from './steps/HoursStep';
import { TravelTimeStep } from './steps/TravelTimeStep';
import { CalendarStep } from './steps/CalendarStep';
import { ONBOARDING_STEP } from '../../business/onboarding/OnboardingManager';

const saveErrorMessage = (error: unknown): string => {
  const err = error as { body?: { error?: { message?: string } }; message?: string } | undefined;
  return (
    err?.body?.error?.message ||
    err?.message ||
    "We couldn't save your changes. Please check your connection and try again."
  );
};

const STEP_COMPONENTS: Record<number, React.FC> = {
  [ONBOARDING_STEP.BRAND]: BrandStep,
  [ONBOARDING_STEP.PROFILE]: ProfileStep,
  [ONBOARDING_STEP.HOME]: HomeAddressStep,
  [ONBOARDING_STEP.BASE]: BaseLocationStep,
  [ONBOARDING_STEP.RADIUS]: RadiusStep,
  [ONBOARDING_STEP.HOURS]: HoursStep,
  [ONBOARDING_STEP.TRAVEL]: TravelTimeStep,
  [ONBOARDING_STEP.CALENDAR]: CalendarStep,
};

export const OnboardingWizard: React.FC = () => {
  const {
    currentStep,
    steps,
    nextStep,
    previousStep,
    canProceed,
    getStepSaver,
    isModalOpen,
    closeModal,
    isInitializing,
    initializeProgress,
  } = useOnboarding();

  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    const userId = user?.profile?.sub;
    if (userId) initializeProgress(userId);
  }, [user?.profile?.sub, initializeProgress]);

  // A failure belongs to the step it happened on; moving away clears it.
  useEffect(() => {
    setSaveError(null);
  }, [currentStep]);

  const finish = useCallback(async () => {
    try {
      await UserService.postApiAppUserCompleteOnboarding();
    } catch (error) {
      console.error('Failed to mark onboarding complete:', error);
    }
    // Hard refresh so every context (including the 2FA guard) reinitializes
    // cleanly. Land on Booking Forms so new users see their form immediately.
    closeModal();
    window.location.href = '/booking-forms';
  }, [closeModal]);

  const handleNext = useCallback(async () => {
    if (isSaving) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      await getStepSaver(currentStep)?.();

      if (currentStep === steps.length - 1) {
        await finish();
      } else {
        nextStep();
      }
    } catch (error) {
      console.error('Failed to save onboarding step:', error);
      setSaveError(saveErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, getStepSaver, currentStep, steps.length, finish, nextStep]);

  if (isInitializing) {
    return (
      <OnboardingLayout dismissible={isModalOpen}>
        <div className="flex flex-col items-center justify-center gap-3 py-20">
          <Loader2 className="h-8 w-8 animate-spin text-accent-600" />
          <p className="text-sm text-ink-500">Picking up where you left off…</p>
        </div>
      </OnboardingLayout>
    );
  }

  const StepComponent = STEP_COMPONENTS[currentStep] ?? BrandStep;
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  return (
    <OnboardingLayout
      dismissible={isModalOpen}
      footer={
        <div className="space-y-3">
          {saveError && (
            <div
              role="alert"
              className="flex items-start gap-2.5 rounded-lg border border-danger-200 bg-danger-50 p-3"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-danger-600" />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-danger-800">
                  We couldn't save this step
                </p>
                <p className="mt-0.5 text-xs text-danger-700">{saveError}</p>
              </div>
              <Button variant="secondary" size="sm" onClick={handleNext} disabled={isSaving}>
                Try again
              </Button>
            </div>
          )}

          {/* Continue is the wider target and sits on the right on every
              breakpoint, so the button under the thumb never changes meaning
              between steps. */}
          <div className="flex items-center gap-3">
            {!isFirstStep && (
              <Button variant="secondary" onClick={previousStep} disabled={isSaving}>
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            )}

            <Button
              onClick={handleNext}
              disabled={!canProceed}
              loading={isSaving}
              className="flex-1 sm:flex-none sm:ml-auto sm:min-w-[10rem]"
            >
              {isSaving ? (
                'Saving…'
              ) : isLastStep ? (
                <>
                  <Sparkles className="h-4 w-4" />
                  Get started
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          </div>

          {!canProceed && !saveError && (
            <p className="text-center text-xs text-ink-400">
              Finish this step to continue. Everything can be changed later in Settings.
            </p>
          )}
        </div>
      }
    >
      <StepComponent />
    </OnboardingLayout>
  );
};
